/* eslint-disable jsx-a11y/no-redundant-roles */
"use client"

import { useEffect } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { verifyPinV1 } from "@/lib/client/api/login"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import OTPInput from "@/components/input-otp"

import { AuthStepProps } from "../types"

// PIN validation schema
const pinFormSchema = z.object({
  pin: z
    .string()
    .min(6, { message: "Mã PIN phải có 6 chữ số" })
    .max(6, { message: "Mã PIN phải có 6 chữ số" })
    .regex(/^[0-9]+$/, { message: "Mã PIN chỉ được chứa chữ số" }),
})

export function PinStep({
  phoneNumber,
  onSuccess,
  startResendCooldown,
}: AuthStepProps) {
  // PIN form
  const form = useForm<z.infer<typeof pinFormSchema>>({
    resolver: zodResolver(pinFormSchema),
    defaultValues: {
      pin: "",
    },
  })

  const isSubmitting = form.formState.isSubmitting

  // Auto-focus on PIN input when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      // Find the PIN input by its ID and focus it
      const pinInput = document.getElementById("pin-input")
      if (pinInput) {
        pinInput.focus()
      }
    }, 100) // Small delay to ensure the component is fully rendered

    return () => clearTimeout(timer)
  }, [])

  // Handle PIN verification
  async function handlePinSubmit(values: z.infer<typeof pinFormSchema>) {
    try {
      // Call the verify PIN API
      const response = await verifyPinV1(phoneNumber, values.pin)

      if (response.code === 143) {
        if (response.login_type === 1) {
          onSuccess("OTP", response)
          startResendCooldown()
        } else if (response.login_type === 2) {
          onSuccess("PIN", response)
        } else if (response.access_token) {
          onSuccess("SUCCESS", response)
        }
      } else {
        toast.error(
          response.msg || "Xác thực PIN không thành công. Vui lòng thử lại."
        )
      }
    } catch (err) {
      toast.error("Có lỗi xảy ra. Vui lòng thử lại sau.")
      console.error(err)
    }
  }

  // Auto-submit PIN when it reaches 6 digits
  useEffect(() => {
    const pinValue = form.watch("pin")
    if (pinValue && pinValue.length === 6 && !isSubmitting) {
      form.handleSubmit(handlePinSubmit)()
      form.reset()
    }
  }, [form.watch("pin"), isSubmitting, form])

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handlePinSubmit)}
        className="space-y-6 p-6 pt-2"
        data-testid="pin-step-form"
        role="form"
      >
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="pin"
            render={({ field }) => (
              <FormItem className="w-full pb-12">
                <FormControl>
                  {/* data-testid moved to slot container for test reliability */}
                  <OTPInput
                    id="pin-input"
                    containerClassName="flex"
                    maxLength={6}
                    masked={true}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            variant="link"
            className="self-start text-lg font-medium transform -translate-x-4 text-blue-700"
            data-testid="forgot-password-button"
          >
            Quên mật khẩu?
          </Button>
        </div>
      </form>
    </Form>
  )
}
